import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import styles from "./datasetManager.module.css";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import SimpleBackIcon from "../../assets/simpleBackIcon.svg";
import DeleteIcon from "../../assets/Stop.svg";
import { useHistory, useLocation } from "react-router-dom";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { AuthContext } from "../../contexts/auth-context";
import InfoPopup from "../InfoPopup";
import PageNotFound from "../PageNotFound";
import InforIcon from "../../assets/InfoCircleIcon";
import config from "../../config";
import LayoutContainer from "../LayoutContainer";
import SearchableDropdown from "../SearchableDropdown";

const DatasetManager = () => {
  const {
    // getNumRecords,
    deleteSchema,
    appMetadata,
    // getrecompute,
    getorganizationById,
    getDatasourceByFedId,
    getBucketsCounts,
    getOrganizations,
    getConfig,
  } = useContext(AuthContext);
  const adminAppUrl = config.adminAppUrl;

  const history = useHistory();
  const location = useLocation<{ selectedOrgId?: string }>();
  const [datasets, setDatasets] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const loadStartRef = useRef<number>(Date.now());
  const MIN_SHIMMER_MS = 800;
  const finishLoading = useCallback(() => {
    const delay = Math.max(
      0,
      MIN_SHIMMER_MS - (Date.now() - loadStartRef.current),
    );
    setTimeout(() => setLoading(false), delay);
  }, []);
  const startLoading = useCallback(() => {
    loadStartRef.current = Date.now();
    setLoading(true);
  }, []);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState({
    id: "",
    name: "",
    attributes: [],
    identifier_type: "",
    canonical_identity_type: "",
    user_details: {
      username: "",
      organization_name: "",
    },
  });
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();

  const [selectedOrgName, setSelectedOrgName] = useState<string>("");
  const [selectedOrg, setSelectedOrg] = useState<string>(
    () => location.state?.selectedOrgId ?? "",
  );
  const { setBreadcrumbItems } = useBreadcrumb();
  const sessionFederationId =
    localStorage.getItem("federationkey") === "null"
      ? null
      : localStorage.getItem("federationkey");
  var userOrganization;
  let tenantObj = {};
  tenantObj = appMetadata && Object.values(appMetadata);
  userOrganization = tenantObj[0]?.organization;
  const [isDataSourcePresent, setIsDataSourcePresent] = useState(false);
  const fetchDataSourceByIdFn = useAsyncFn({
    deferFn: getDatasourceByFedId,
    organizationId: userOrganization,
  });

  const getBucketsCountsFn = useAsyncFn({
    deferFn: getBucketsCounts,
    federationId: localStorage.getItem("federationkey"),
    organizationId: userOrganization,
  });
  const deleteSchemaFn = useAsyncFn({
    deferFn: deleteSchema,
  });

  const getorganizationByIdFn = useAsyncFn({
    promiseFn: getorganizationById,
    organizationID: userOrganization,
  });

  const getConfigFn = useAsyncFn({
    deferFn: getConfig,
    organizationId: userOrganization,
  });

  useEffect(() => {
    if (userOrganization) {
      getConfigFn.run({ organizationId: userOrganization }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrganization]);

  const identityTypeMap = new Map<string, string>(
    (getConfigFn.data?.message?.canonical_identity_types ?? []).map(
      ({ key, value }: { key: string; value: string }) => [key, value],
    ),
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isPlatformOrAppAdmin) return; // admin fetch is driven by the [selectedOrg] effect
    getBucketsCountsFn.run({
      federationId: localStorage.getItem("federationkey"),
      organizationId: userOrganization,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getBucketsCountsFn.data) {
      const items = (getBucketsCountsFn.data.Items || []).map(
        (dataset: any) => ({
          ...dataset,
          attributes: (dataset.attributes || []).filter((a: any) => a.name),
        }),
      );
      setDatasets(items);
      setSelectedDataset(items[0]);
      finishLoading();
    } else if (getBucketsCountsFn.isRejected) {
      finishLoading();
    }
  }, [getBucketsCountsFn.data, getBucketsCountsFn.isRejected]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/dataset-manager")),
      )?.label;

    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      cp: sessionFederationId === null ? true : false,
      cr: sessionFederationId === null ? true : false,
      pr: sessionFederationId === null ? true : false,
      ca: false,
      ah: sessionFederationId === null ? true : false,
      dm: sessionFederationId === null ? true : false,
    });
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Federation Selection", path: "/federation-selection" },
      { label: "Dataset Manager", path: "/dataset-manager" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteDataset = async () => {
    setOpenDeletePopup(false);
    setLoading(true);

    try {
      await deleteSchemaFn.run({
        userBucketId: selectedDataset?.id,
        organizationId: isPlatformOrAppAdmin ? selectedOrg : userOrganization,
      });

      const remaining = datasets.filter(
        (dataset) => dataset.id !== selectedDataset?.id,
      );
      setDatasets(remaining);

      if (remaining.length > 0) {
        const deletedIndex = datasets.findIndex(
          (d) => d.id === selectedDataset?.id,
        );
        const nextIndex = Math.min(deletedIndex, remaining.length - 1);
        setSelectedDataset(remaining[nextIndex]);
      } else {
        setSelectedDataset({
          id: "",
          name: "",
          attributes: [],
          identifier_type: "",
          canonical_identity_type: "",
          user_details: {
            username: "",
            organization_name: "",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting dataset:", error);
    } finally {
      setLoading(false);
    }
  };

  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );

  const authRole =
    (appMetadata && (Object.values(appMetadata) as any[])[0]?.roles?.[0]) || "";
  const isPlatformOrAppAdmin =
    authRole === config.superUserRole ||
    authRole === config.applicationAdminRole;

  useEffect(() => {
    if (isPlatformOrAppAdmin && selectedOrg) {
      startLoading();
      localStorage.setItem("selectedOrganizationId", selectedOrg);
      const organizationDataRequest = getorganizationByIdFn
        .run({ organizationID: selectedOrg })
        .catch((err) => {
          console.error("Handled gracefully:", err);
        });

      const bucketsCountsRequest = getBucketsCountsFn.run({
        federationId: localStorage.getItem("federationkey"),
        organizationId: selectedOrg,
      });

      Promise.all([organizationDataRequest, bucketsCountsRequest]).finally(
        () => {
          finishLoading();
        },
      );
    } else if (isPlatformOrAppAdmin && !selectedOrg) {
      finishLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isPlatformOrAppAdmin]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const organizationId = isPlatformOrAppAdmin
      ? selectedOrg
      : userOrganization;
    if (!organizationId) {
      setIsDataSourcePresent(false);
      return;
    }

    let isMounted = true;

    fetchDataSourceByIdFn
      .run({ organizationId })
      .then((data) => {
        if (!isMounted) {
          return;
        }
        const present =
          (data?.Count ?? 0) > 0 || (data?.Items?.length ?? 0) > 0;
        setIsDataSourcePresent(present);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        setIsDataSourcePresent(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isPlatformOrAppAdmin, selectedOrg, userOrganization]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOrgSelect = (org: { id: string; name: string }) => {
    setSelectedOrg(org.id);
    setSelectedOrgName(org.name);
    localStorage.setItem("selectedOrganizationName", org.name);
  };
  const handleOrgClear = () => {
    setSelectedOrg("");
    setSelectedOrgName("");
    localStorage.removeItem("selectedOrganizationName");
  };
  if (storedPermissions?.datamanager === "no") {
    return (
      <div style={{ paddingTop: "15vh" }}>
        <PageNotFound />
      </div>
    );
  }

  if (
    !isPlatformOrAppAdmin &&
    !fetchDataSourceByIdFn.isPending &&
    !isDataSourcePresent
  ) {
    return (
      <LayoutContainer>
        <div className={styles.datasetContainer}>
          <PageNotFound
            message="To create a dataset, a datasource must be configured first. Please go to the Admin app and set up a datasource configuration for your  organization."
            buttonText="Go to Admin App"
            onButtonClick={() => {
              window.open(adminAppUrl, "_blank", "noopener,noreferrer");
            }}
          />
        </div>
      </LayoutContainer>
    );
  }

  if (
    loading ||
    getBucketsCountsFn.isPending ||
    fetchDataSourceByIdFn.isPending
  ) {
    return (
      <LayoutContainer>
        <div className={styles.datasetContainer}>
          <div className={styles.container}>
            <div className={styles.shimmerWrapper}>
              <div className={styles.shimmerToolbarRow}>
                <div
                  className={styles.shimmerBar}
                  style={{ width: 64, height: 12 }}
                />
                <div
                  className={styles.shimmerBar}
                  style={{ width: 108, height: 30, borderRadius: 4 }}
                />
              </div>
              <div className={styles.shimmerPillsRow}>
                {[88, 106, 72, 118, 82].map((w, i) => (
                  <div
                    key={i}
                    className={`${styles.shimmerBar} ${styles.shimmerPillShape}`}
                    style={{ width: w }}
                  />
                ))}
              </div>
              <div className={styles.shimmerCardBody}>
                {[58, 42, 68, 50, 74, 45].map((pct, i) => (
                  <div
                    key={i}
                    className={styles.shimmerBar}
                    style={{ width: `${pct}%`, height: 14 }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </LayoutContainer>
    );
  }

  if (
    isPlatformOrAppAdmin &&
    selectedOrg &&
    !loading &&
    !fetchDataSourceByIdFn.isPending &&
    !isDataSourcePresent
  ) {
    return (
      <LayoutContainer>
        <div className={styles.datasetContainer}>
          <div className={styles.headerContainer}>
            <button
              className={styles.backButton}
              onClick={() => history.push("/federation-selection")}
            >
              <img src={SimpleBackIcon} alt="back" />
              <span className={styles.backButtonText}>Back</span>
            </button>

            <InforIcon
              className={styles.infoIcon}
              onClick={() => setShowInfoPopup(true)}
            />
            {showInfoPopup && (
              <InfoPopup
                displayText={
                  "A dataset defines the tables available for building queries. Create one by selecting its source tables, then assign it to a federation so it can be used in that federation's queries. Set attributes on the dataset to control which fields show up in the query builder. You can come back to edit a dataset's tables and attributes, or delete it, at any time."
                }
                isOpen={showInfoPopup}
                onClose={() => setShowInfoPopup(false)}
                closeButtonLabel="Got it"
                headerText="Information"
                freeText
              />
            )}
          </div>
          {loading ? (
            <div className={styles.container}>
              <div className={styles.shimmerWrapper}>
                <div className={styles.shimmerToolbarRow}>
                  <div
                    className={styles.shimmerBar}
                    style={{ width: 64, height: 12 }}
                  />
                  <div
                    className={styles.shimmerBar}
                    style={{ width: 108, height: 30, borderRadius: 4 }}
                  />
                </div>
                <div className={styles.shimmerPillsRow}>
                  {[88, 106, 72, 118, 82].map((w, i) => (
                    <div
                      key={i}
                      className={`${styles.shimmerBar} ${styles.shimmerPillShape}`}
                      style={{ width: w }}
                    />
                  ))}
                </div>
                <div className={styles.shimmerCardBody}>
                  {[58, 42, 68, 50, 74, 45].map((pct, i) => (
                    <div
                      key={i}
                      className={styles.shimmerBar}
                      style={{ width: `${pct}%`, height: 14 }}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.containerAdmin}>
              <div className={styles.orgWrapper}>
                {isPlatformOrAppAdmin && (
                  <SearchableDropdown<{ id: string; name: string }>
                    fetchOptions={(name) =>
                      getOrganizations({ args: { name, page_size: 8 } })
                    }
                    onSelect={handleOrgSelect}
                    onClear={handleOrgClear}
                    getLabel={(org) => org.name}
                    getValue={(org) => org.id}
                    selectedValue={selectedOrg}
                    selectedLabel={selectedOrgName}
                    placeholder="Search organization…"
                    className={styles.selectOrg}
                  />
                )}
                {(storedPermissions?.datamanager === "write" ||
                  storedPermissions?.datamanager === "full") && (
                  <button
                    className={styles.addDatasetButton}
                    onClick={() => history.push("/create-dataset")}
                    disabled={true}
                  >
                    + Add Dataset
                  </button>
                )}
              </div>
              <div className={styles.noDataSourceNotice}>
                <p>
                  No datasource is configured for{" "}
                  <strong>
                    {localStorage.getItem("selectedOrganizationName") ||
                      "this organization"}
                  </strong>
                  .
                </p>
                <p>
                  To create datasets for this organization, a datasource must be
                  set up first.{" "}
                  <button
                    className={styles.adminAppLink}
                    onClick={() =>
                      window.open(adminAppUrl, "_blank", "noopener,noreferrer")
                    }
                  >
                    Go to Admin App
                  </button>
                </p>
                <p>
                  You can also switch to a different organization using the
                  dropdown above.
                </p>
              </div>
            </div>
          )}
        </div>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <div className={styles.datasetContainer}>
        {openDeletePopup && (
          <InfoPopup
            isOpen={openDeletePopup}
            headerText="Delete dataset"
            displayText={`Are you sure you want to delete this dataset - ${selectedDataset?.name}?`}
            onClose={handleDeleteDataset}
            onCancelButtonClick={() => setOpenDeletePopup(false)}
            closeButtonLabel="Delete"
            freeText
            showCancelButton
          />
        )}
        <div className={styles.headerContainer}>
          <button
            className={styles.backButton}
            onClick={() => window?.history?.back()}
          >
            <img src={SimpleBackIcon} alt="back" />
            <span className={styles.backButtonText}>Back</span>
          </button>

          <InforIcon
            className={styles.infoIcon}
            onClick={() => setShowInfoPopup(true)}
          />
          {showInfoPopup && (
            <InfoPopup
              displayText={
                "A dataset defines the tables available for building queries. Create one by selecting its source tables, then assign it to a federation so it can be used in that federation's queries. Set attributes on the dataset to control which fields show up in the query builder. You can come back to edit a dataset's tables and attributes, or delete it, at any time."
              }
              isOpen={showInfoPopup}
              onClose={() => setShowInfoPopup(false)}
              closeButtonLabel="Got it"
              headerText="Information"
              freeText
            />
          )}
        </div>
        {loading ? (
          <div className={styles.container}>
            <div className={styles.shimmerWrapper}>
              <div className={styles.shimmerToolbarRow}>
                <div
                  className={styles.shimmerBar}
                  style={{ width: 64, height: 12 }}
                />
                <div
                  className={styles.shimmerBar}
                  style={{ width: 108, height: 30, borderRadius: 4 }}
                />
              </div>
              <div className={styles.shimmerPillsRow}>
                {[88, 106, 72, 118, 82].map((w, i) => (
                  <div
                    key={i}
                    className={`${styles.shimmerBar} ${styles.shimmerPillShape}`}
                    style={{ width: w }}
                  />
                ))}
              </div>
              <div className={styles.shimmerCardBody}>
                {[58, 42, 68, 50, 74, 45].map((pct, i) => (
                  <div
                    key={i}
                    className={styles.shimmerBar}
                    style={{ width: `${pct}%`, height: 14 }}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.toolbar}>
              <div className={styles.toolbarHeader}>
                <span className={styles.sectionLabel}>Datasets</span>
                <div className={styles.toolbarActions}>
                  {isPlatformOrAppAdmin && (
                    <SearchableDropdown<{ id: string; name: string }>
                      fetchOptions={(name) =>
                        getOrganizations({ args: { name, page_size: 8 } })
                      }
                      onSelect={handleOrgSelect}
                      onClear={handleOrgClear}
                      getLabel={(org) => org.name}
                      getValue={(org) => org.id}
                      selectedValue={selectedOrg}
                      selectedLabel={selectedOrgName}
                      placeholder="Search organization…"
                      className={styles.selectOrg}
                    />
                  )}
                  {(storedPermissions?.datamanager === "write" ||
                    storedPermissions?.datamanager === "full") && (
                    <button
                      className={styles.addDatasetButton}
                      onClick={() => history.push("/create-dataset")}
                      disabled={isPlatformOrAppAdmin && !selectedOrg}
                    >
                      + Add Dataset
                    </button>
                  )}
                </div>
              </div>
              <div className={styles.datasetPills}>
                {datasets?.map((dataset) => (
                  <button
                    key={dataset.id}
                    className={`${styles.datasetPill} ${
                      selectedDataset?.id === dataset.id
                        ? styles.datasetPillActive
                        : ""
                    }`}
                    onClick={() => setSelectedDataset(dataset)}
                  >
                    {dataset.name}
                    {dataset.attributes?.length > 0 && (
                      <span className={styles.pillCount}>
                        {dataset.attributes.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            {isPlatformOrAppAdmin && !selectedOrg ? (
              <div className={styles.emptyState}>
                Please select an organization to view its datasets.
              </div>
            ) : datasets.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No datasets configured yet.</p>
                {(storedPermissions?.datamanager === "write" ||
                  storedPermissions?.datamanager === "full") && (
                  <button
                    className={styles.emptyStateCta}
                    onClick={() => history.push("/create-dataset")}
                  >
                    + Add Dataset
                  </button>
                )}
              </div>
            ) : !selectedDataset ? (
              <div className={styles.emptyState}>
                Select a dataset above to view its attributes.
              </div>
            ) : (
              <div className={styles.datasetTable}>
                <div className={styles.datasetContextHeader}>
                  <span className={styles.datasetContextName}>
                    {selectedDataset.name}
                  </span>
                  <span className={styles.attributeCount}>
                    · {selectedDataset.attributes.length} attribute
                    {selectedDataset.attributes.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className={styles.datasetMeta}>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>Name:</span>
                    <span className={styles.datasetMetaValue}>
                      {selectedDataset.name || "—"}
                    </span>
                  </span>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>
                      Record Identifier:
                    </span>
                    <span className={styles.datasetMetaValue}>
                      {selectedDataset.identifier_type || "—"}
                    </span>
                  </span>
                  <span className={styles.datasetMetaItem}>
                    <span className={styles.datasetMetaLabel}>ID Type:</span>
                    <span className={styles.datasetMetaValue}>
                      {(identityTypeMap.get(
                        selectedDataset.canonical_identity_type,
                      ) ??
                        selectedDataset.canonical_identity_type) ||
                        "—"}
                    </span>
                  </span>
                </div>
                {!selectedDataset.attributes ||
                selectedDataset.attributes.length === 0 ? (
                  <div className={styles.emptyState}>
                    This dataset has no attributes configured yet.
                  </div>
                ) : (
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Attributes</th>
                          <th>Data Type</th>
                          <th>Values</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDataset.attributes.map((attribute, index) => (
                          <tr key={index}>
                            <td>{attribute.name}</td>
                            <td>
                              {attribute.type ? (
                                <span className={styles.typeBadge}>
                                  {attribute.type}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>
                              {attribute.values &&
                              attribute.values.length > 0 ? (
                                <div className={styles.valuePills}>
                                  {attribute.values.map(
                                    (v: string | boolean | null, i: number) => (
                                      <span
                                        key={i}
                                        className={styles.valuePill}
                                      >
                                        {v != null ? String(v) : "—"}
                                      </span>
                                    ),
                                  )}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className={styles.actionButtons}>
                  <div className={styles.createdBy}>
                    Created by:
                    <span className={styles.createdByUser}>
                      {selectedDataset?.user_details?.username} (
                      {selectedDataset?.user_details?.organization_name})
                    </span>
                  </div>
                  {(storedPermissions?.datamanager === "full") && (
                    <button
                      className={styles.deleteButton}
                      onClick={() => setOpenDeletePopup(true)}
                    >
                      <img src={DeleteIcon} alt="delete" />
                      Delete Dataset
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </LayoutContainer>
  );
};

export default DatasetManager;
